/**
 * `RewardedAdButton` — the opt-in offer, which is the STAR format.
 *
 * Rules it embodies:
 *   - **Never auto-launched**: only plays upon clicking.
 *   - The button ALWAYS says what is earned before playing anything.
 *   - If the gate closes (daily cap, backoff, premium, network off) the button
 *     **is not rendered** — not shown disabled nor explained why.
 *   - If the ad fails, soft toast and the button disappears for this session: never leaves
 *     the user staring at a spinner.
 */
import { msg } from "@lingui/core/macro";
import { createEffect, createSignal, Show } from "solid-js";
import { Gift } from "@fleet/icons";
import {
  type AdReward,
  LISTTO_AD_PLACEMENTS,
  type ListtoPlacementId,
  placementDef,
  REWARD_ATTACHMENT_QUOTA,
  REWARD_FONT_UNLOCK,
  REWARD_STREAK_FREEZE,
  REWARD_THEME_UNLOCK,
} from "@listto/shared";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { useAds } from "../ads-context";

interface RewardedAdButtonProps {
  placement: ListtoPlacementId;
  /** Called ONLY if reward was actually granted. */
  onReward?: (reward: AdReward) => void;
}

/** Reward text (reactive i18n): what the user earns, explicit. */
function rewardLabel(
  t: (d: ReturnType<typeof msg>) => string,
  reward: AdReward | undefined,
): string {
  switch (reward?.type) {
    case REWARD_STREAK_FREEZE: {
      return t(msg`Watch an ad → +1 streak saver`);
    }
    case REWARD_THEME_UNLOCK: {
      return t(msg`Watch an ad → premium theme for 24h`);
    }
    case REWARD_FONT_UNLOCK: {
      return t(msg`Watch an ad → premium font for 24h`);
    }
    case REWARD_ATTACHMENT_QUOTA: {
      return t(msg`Watch an ad → more attachments for 24h`);
    }
    default: {
      return t(msg`Watch an ad for a reward`);
    }
  }
}

export function RewardedAdButton(props: RewardedAdButtonProps) {
  const { t } = useI18n();
  const ads = useAds();
  const [failed, setFailed] = createSignal(false);
  const [busy, setBusy] = createSignal(false);

  const def = () => placementDef(LISTTO_AD_PLACEMENTS, props.placement);
  const visible = () => !failed() && ads?.gate(props.placement).allow === true;

  // Best-effort preload on mount + SHOWN offer is recorded (backoff).
  createEffect((counted: boolean | undefined) => {
    if (!visible() || counted) {
      return counted ?? false;
    }
    ads?.recordOffer(props.placement);
    void ads?.provider().prepare("rewarded", props.placement);
    return true;
  });

  const play = async () => {
    setBusy(true);
    try {
      const reward = await ads?.showRewarded(props.placement);
      if (reward) {
        props.onReward?.(reward);
        showToast(t(msg`Reward unlocked`), "success");
        return;
      }
      // No reward = ad did not complete or no fill: withdraw the
      // offer for this session instead of insisting.
      setFailed(true);
      showToast(t(msg`No ad available right now`), "info");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Show when={visible()}>
      <Button
        variant="outline"
        loading={busy()}
        onClick={() => void play()}
        data-testid={`rewarded-ad-${props.placement}`}
      >
        <Gift class="size-4" aria-hidden="true" />
        {rewardLabel(t, def()?.reward)}
      </Button>
    </Show>
  );
}
